import { ApiService } from './api';
import { Product, CreateProductData, UpdateProductData } from '@/models/product';
import { ApiResponse } from '@/models/common';
import { getCachedProducts, cacheProducts, clearProductsCache } from '@/utils/sessionManager';
import { ApiEndpoints, Endpoints } from '@/models/endpoints';

/**
 * ProductService with Caching Support
 * 
 * This service implements intelligent caching for product data to improve performance
 * and reduce API calls. All methods that fetch data support an optional `force` parameter.
 * 
 * Caching Behavior:
 * - force=false (default): Check cache first, return cached data if available
 * - force=true: Ignore cache and fetch fresh data from API
 * 
 * Cache is automatically cleared when products are created, updated, or deleted.
 * Cache TTL is set to 10 minutes in sessionManager.
 * 
 * Usage Examples:
 * - ProductService.getProducts() - Uses cache if available
 * - ProductService.getProducts(true) - Forces API call
 * - ProductService.refreshCache() - Clears cache and fetches fresh data
 */

export class ProductService {
  // Get all products (no filters, returns total data)
  static async getProducts(force: boolean = false): Promise<ApiResponse<Product[]>> {
    // Check cache first if not forcing refresh
    if (!force) {
      const cachedProducts = getCachedProducts();
      if (cachedProducts && Array.isArray(cachedProducts)) {
    
        return {
          success: true,
          data: cachedProducts as Product[],
          message: 'Loaded from cache'
        };
      }
    }


    const response = await ApiService.get<Product[]>(ApiEndpoints.PRODUCTS);
    
    // Cache the results if successful
    if (response.success && response.data) {
  
      cacheProducts(response.data);
    }
    
    return response;
  }

  // Get product by ID
  static async getProductById(id: string, force: boolean = false): Promise<ApiResponse<Product>> {
    // For individual product, we could implement caching but it's less critical
    // For now, always fetch from API
    return ApiService.get<Product>(Endpoints.getProduct(id));
  }

  // Get product by item code
  static async getProductByCode(itemCode: string, force: boolean = false): Promise<ApiResponse<Product>> {
    // Check cache first if not forcing refresh
    if (!force) {
      const cachedProducts = getCachedProducts();
      if (cachedProducts && Array.isArray(cachedProducts)) {
        const product = (cachedProducts as Product[]).find(p => p.item_code === itemCode);
        if (product) {
      
          return {
            success: true,
            data: product,
            message: 'Loaded from cache'
          };
        }
      }
    }


    return ApiService.get<Product>(Endpoints.getProductByCode(itemCode));
  }

  // Create new product
  static async createProduct(productData: CreateProductData): Promise<ApiResponse<Product>> {
    const response = await ApiService.post<Product>(ApiEndpoints.PRODUCTS, productData);
    if (response.success) {
      // Clear cache when new product is created
  
      clearProductsCache();
    }
    return response;
  }

  // Update product
  static async updateProduct(id: string, productData: UpdateProductData): Promise<ApiResponse<Product>> {
    const response = await ApiService.put<Product>(Endpoints.updateProduct(id), productData);

    if (response.success) {
      // Clear cache when product is updated
  
      clearProductsCache();
    }
    return response;
  }

  // Delete product (soft delete by setting is_deleted to 1)
  static async deleteProduct(id: string): Promise<ApiResponse<{ message: string }>> {
    const response = await ApiService.delete<{ message: string }>(Endpoints.deleteProduct(id));
    if (response.success) {
      // Clear cache when product is deleted
      clearProductsCache();
    }
    return response;
  }

  // Search products by name or code (client-side filtering)
  static async searchProducts(query: string, force: boolean = false): Promise<ApiResponse<Product[]>> {
    // Check cache first if not forcing refresh
    if (!force) {
      const cachedProducts = getCachedProducts();
      if (cachedProducts && Array.isArray(cachedProducts)) {
        const searchResults = (cachedProducts as Product[]).filter(product => 
          product.item_code.toLowerCase().includes(query.toLowerCase()) ||
          product.item_name.toLowerCase().includes(query.toLowerCase())
        );
        if (searchResults.length > 0) {
          return {
            success: true,
            data: searchResults,
            message: 'Loaded from cache'
          };
        }
      }
    }

    return ApiService.get<Product[]>(Endpoints.searchProducts(query));
  }

  // Get products for dropdown/autocomplete (only non-deleted)
  static async getProductsForSelect(force: boolean = false): Promise<ApiResponse<Product[]>> {
    
    // Check cache first if not forcing refresh
    if (!force) {
      const cachedProducts = getCachedProducts();
      if (cachedProducts && Array.isArray(cachedProducts)) {
        return {
          success: true,
          data: cachedProducts as Product[],
          message: 'Loaded from cache'
        };
      }
    }

    // Use the standard getProducts API and filter non-deleted products
    const response = await ApiService.get<Product[]>(ApiEndpoints.PRODUCTS);
    
    if (response.success) {
      
      // Handle different response structures
      let productsData: Product[];
      
      if (Array.isArray(response.data)) {
        // Direct array response
        productsData = response.data;
      } else {
        console.error('Unexpected response structure:', response.data);
        return {
          success: false,
          data: [],
          error: 'Unexpected response structure'
        };
      }
      
      // Filter out deleted products
      const activeProducts = productsData.filter(product => product.is_deleted === 0);
      
      // Cache the results
      cacheProducts(activeProducts);
      
      return {
        success: true,
        data: activeProducts,
        message: 'Success'
      };
    }
    
    return {
      success: false,
      data: [],
      error: response.error || 'Failed to load products'
    };
  }

  // Bulk delete products (soft delete)
  static async bulkDeleteProducts(ids: string[]): Promise<ApiResponse<{ message: string; deletedCount: number }>> {
    const response = await ApiService.post<{ message: string; deletedCount: number }>(ApiEndpoints.PRODUCTS_BULK_DELETE, { ids });
    if (response.success) {
      // Clear cache when products are deleted
      clearProductsCache();
    }
    return response;
  }

  // Check if item code exists
  static async checkProductCodeExists(itemCode: string, force: boolean = false): Promise<ApiResponse<{ exists: boolean }>> {
    // Check cache first if not forcing refresh
    if (!force) {
      const cachedProducts = getCachedProducts();
      if (cachedProducts && Array.isArray(cachedProducts)) {
        const exists = (cachedProducts as Product[]).some(p => p.item_code === itemCode);
        return {
          success: true,
          data: { exists },
          message: 'Checked from cache'
        };
      }
    }

    return ApiService.get<{ exists: boolean }>(Endpoints.checkProductCode(itemCode));
  }

  // Export products to CSV
  static async exportProducts(): Promise<Blob> {
    const response = await fetch(ApiEndpoints.PRODUCTS_EXPORT, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('jwt')}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to export products');
    }
    
    return response.blob();
  }

  // Force refresh cache
  static async refreshCache(): Promise<void> {
    clearProductsCache();
    await this.getProductsForSelect(true);
  }

  // Get cache status
  static getCacheStatus(): { hasCache: boolean; cacheSize: number } {
    const cachedProducts = getCachedProducts();
    return {
      hasCache: !!(cachedProducts && Array.isArray(cachedProducts)),
      cacheSize: (cachedProducts && Array.isArray(cachedProducts)) ? cachedProducts.length : 0
    };
  }
} 